import { useMemo } from "react";
import { IconeColorido } from "./selecao/IconeColorido";
import { iconeDaCategoria } from "./selecao/icones-da-selecao";
import type { AbrangenciaDoServico } from "@bookdental/schemas";
import { ROTULO_DA_ABRANGENCIA, nomeDoMaterial, type EscalaDaBase, type MaterialDaBase, type ServicoEscolhido } from "./configurar/tipos";

/**
 * Passo 3: o que vai pro catálogo, numa tabela por categoria — serviço, dente/arcada, cada
 * material numa linha e as escalas de cor dele. Só leitura; "Editar" volta pro passo 2.
 */
export function PassoConfirmar({
  servicos,
  mapa,
  materiais,
  escalas,
  escalasPorMaterial,
  abrangenciaDe,
  onEditar,
}: {
  servicos: ServicoEscolhido[];
  mapa: Record<string, string[]>;
  materiais: MaterialDaBase[];
  escalas: EscalaDaBase[];
  escalasPorMaterial: Record<string, string[]>;
  abrangenciaDe: (chave: string) => AbrangenciaDoServico;
  onEditar: () => void;
}) {
  const daBase = useMemo(() => new Map(materiais.map((m) => [m.id, m])), [materiais]);
  const nomeDaEscala = useMemo(() => new Map(escalas.map((e) => [e.codigo, e.nome])), [escalas]);
  const categorias = [...new Set(servicos.map((s) => s.categoria))];
  const usados = new Set(servicos.flatMap((s) => mapa[s.chave] ?? []));
  const comEscala = [...usados].filter((m) => (escalasPorMaterial[m] ?? []).length > 0).length;

  const totais = [
    [servicos.length, servicos.length === 1 ? "serviço" : "serviços"],
    [categorias.length, categorias.length === 1 ? "categoria" : "categorias"],
    [usados.size, usados.size === 1 ? "material" : "materiais"],
    [comEscala, comEscala === 1 ? "material com escala definida" : "materiais com escala definida"],
  ] as const;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-foreground">Confira e conclua</h2>
          <p className="text-sm text-zinc-500 dark:text-muted-foreground">
            É o que o dentista vai poder escolher em cada serviço. Material sem escala marcada aceita todas as cores. Tudo isso também se muda
            depois no Catálogo.
          </p>
        </div>
        <button type="button" onClick={onEditar} className="text-sm font-semibold text-brand-700 hover:underline dark:text-primary">
          Editar
        </button>
      </div>

      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {totais.map(([n, rotulo]) => (
          <div key={rotulo} className="rounded-lg border border-zinc-200 bg-white px-3 py-2 shadow-sm dark:border-border dark:bg-card">
            <dt className="sr-only">{rotulo}</dt>
            <dd className="text-xl font-semibold tabular-nums text-zinc-900 dark:text-foreground">{n}</dd>
            <dd className="text-[11.5px] text-zinc-500 dark:text-muted-foreground">{rotulo}</dd>
          </div>
        ))}
      </dl>

      {/* Lista em colunas (pedido do Jun): uma linha por material, o serviço ocupando as linhas dele. */}
      {categorias.map((c) => (
        <section key={c} className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card">
          <div className="flex items-center gap-2.5 border-b border-zinc-100 px-3 py-2.5 dark:border-border">
            <IconeColorido {...iconeDaCategoria(c)} tamanho="sm" />
            <p className="text-[15px] font-semibold text-zinc-900 dark:text-foreground">{c}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="bg-zinc-50 text-left text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:bg-muted/40 dark:text-muted-foreground">
                  <th className="w-[30%] px-3 py-2">Serviço</th>
                  <th className="w-[14%] px-3 py-2">Dente/Arcada</th>
                  <th className="w-[28%] px-3 py-2">Material</th>
                  <th className="px-3 py-2">Escalas de cor</th>
                </tr>
              </thead>
              <tbody>
                {servicos
                  .filter((s) => s.categoria === c)
                  .flatMap((s) => {
                    const ms = mapa[s.chave] ?? [];
                    const linhas = ms.length ? ms : [null];
                    return linhas.map((m, k) => {
                      const es = m ? (escalasPorMaterial[m] ?? []).map((e) => nomeDaEscala.get(e) ?? e) : [];
                      return (
                        <tr key={`${s.chave}:${m ?? "-"}`} className={k === 0 ? "border-t border-zinc-100 dark:border-border/60" : undefined}>
                          {k === 0 && (
                            <>
                              <td rowSpan={linhas.length} className="px-3 py-2 align-top font-medium text-zinc-800 dark:text-foreground/90">
                                {s.nome}
                                {s.proprio && <span className="ml-1.5 text-[10px] font-semibold uppercase text-brand-600">próprio</span>}
                              </td>
                              <td rowSpan={linhas.length} className="px-3 py-2 align-top text-zinc-600 dark:text-muted-foreground">
                                {ROTULO_DA_ABRANGENCIA[abrangenciaDe(s.chave)]}
                              </td>
                            </>
                          )}
                          <td className="px-3 py-1.5 text-zinc-700 dark:text-foreground/90">
                            {m ? nomeDoMaterial(m, daBase) : <span className="text-zinc-400 dark:text-muted-foreground">Sem material</span>}
                          </td>
                          <td className="px-3 py-1.5 text-zinc-600 dark:text-muted-foreground">
                            {m ? (es.length ? es.join(", ") : <span className="text-zinc-400 dark:text-muted-foreground">Todas as cores</span>) : "—"}
                          </td>
                        </tr>
                      );
                    });
                  })}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}
